/**
 * Contenu du guide client « Guide-moi » — 7 langues (fr, en, es, pt-BR, ar, ar-SY, zh), exhaustif.
 *
 * Source de vérité unique consommée par : la page /aide, la visite interactive et la vidéo.
 * Couvre TOUTE action que le client peut ou ne peut pas faire, côté vitrine publique et espace client.
 * Chaque parcours vérifié contre le code réel (login par téléphone, acompte 50 %, identité obligatoire
 * avant devis, pas d'annulation de commande, etc.).
 *
 * Forme d'un parcours :
 *   { id, icon, title:{…}, where:{…}, steps:{…:[]}, can:{…:[]}, cannot:{…:[]} }
 * Chaque champ texte porte les 7 clés de langue : fr, en, es, 'pt-BR', ar, 'ar-SY', zh.
 * `icon` = nom d'une icône lucide-react (résolue par la page).
 */

export const AIDE_SECTIONS = [
  // ─────────────────────────────── A. VITRINE PUBLIQUE ───────────────────────────────
  {
    id: 'vitrine',
    icon: 'Store',
    title: {
      fr: 'La vitrine (sans compte)',
      en: 'The public site (no account)',
      es: 'El sitio público (sin cuenta)',
      'pt-BR': 'O site público (sem conta)',
      ar: 'الواجهة العامة (بدون حساب)',
      'ar-SY': 'الموقع العام (بدون حساب)',
      zh: '公开网站（无需账户）',
    },
    intro: {
      fr: "Tout ce que vous pouvez faire sur la page d'accueil, sans être connecté.",
      en: 'Everything you can do on the homepage, without signing in.',
      es: 'Todo lo que puede hacer en la página de inicio, sin iniciar sesión.',
      'pt-BR': 'Tudo o que você pode fazer na página inicial, sem fazer login.',
      ar: 'كل ما يمكنك فعله في الصفحة الرئيسية دون تسجيل الدخول.',
      'ar-SY': 'كل شي فيك تعملو بالصفحة الرئيسية بدون ما تسجّل دخول.',
      zh: '无需登录即可在首页完成的所有操作。',
    },
    items: [
      {
        id: 'promo',
        icon: 'Sparkles',
        title: {
          fr: 'Promotions du jour',
          en: 'Deals of the day',
          es: 'Ofertas del día',
          'pt-BR': 'Ofertas do dia',
          ar: 'عروض اليوم',
          'ar-SY': 'عروض اليوم',
          zh: '每日优惠',
        },
        where: {
          fr: "En haut de la page d'accueil.",
          en: 'At the top of the homepage.',
          es: 'En la parte superior de la página de inicio.',
          'pt-BR': 'No topo da página inicial.',
          ar: 'في أعلى الصفحة الرئيسية.',
          'ar-SY': 'بأعلى الصفحة الرئيسية.',
          zh: '在首页顶部。',
        },
        steps: {
          fr: ['La bannière affiche le « coup de cœur » et les promos du jour, avec le prix barré et le prix remisé.'],
          en: ['The banner shows the daily highlight and deals, with the old price crossed out and the discounted price.'],
          es: ['El banner muestra el «favorito» y las ofertas del día, con el precio tachado y el precio rebajado.'],
          'pt-BR': ['O banner mostra o «destaque do dia» e as promoções, com o preço antigo riscado e o preço com desconto.'],
          ar: ['تعرض اللافتة «المفضّل» وعروض اليوم، مع السعر القديم مشطوبًا والسعر بعد الخصم.'],
          'ar-SY': ['البانر بيعرض «المفضّل» وعروض اليوم، مع السعر القديم مشطوب والسعر بعد الخصم.'],
          zh: ['横幅展示“每日推荐”和当日优惠，标出划掉的原价和折后价。'],
        },
        can: {
          fr: ['Voir les offres du jour.'],
          en: ['See today’s offers.'],
          es: ['Ver las ofertas del día.'],
          'pt-BR': ['Ver as ofertas do dia.'],
          ar: ['رؤية عروض اليوم.'],
          'ar-SY': ['تشوف عروض اليوم.'],
          zh: ['查看当日优惠。'],
        },
        cannot: {
          fr: ['Acheter depuis la bannière (l’achat se fait en boutique ou dans la Boutique de l’espace client).'],
          en: ['Buy from the banner (purchase happens in store or in the client-area Shop).'],
          es: ['Comprar desde el banner (la compra se realiza en la tienda o en la Tienda del espacio de cliente).'],
          'pt-BR': ['Comprar pelo banner (a compra é feita na loja física ou na Loja da área do cliente).'],
          ar: ['الشراء من اللافتة (يتم الشراء في المتجر أو في متجر مساحة العميل).'],
          'ar-SY': ['تشتري من البانر (الشرا بيصير بالمحل أو من متجر مساحة الزبون).'],
          zh: ['从横幅直接购买（购买在门店或客户专区的商店中完成）。'],
        },
      },
      {
        id: 'langue',
        icon: 'Languages',
        title: {
          fr: 'Changer de langue',
          en: 'Change language',
          es: 'Cambiar de idioma',
          'pt-BR': 'Mudar de idioma',
          ar: 'تغيير اللغة',
          'ar-SY': 'تغيير اللغة',
          zh: '切换语言',
        },
        where: {
          fr: 'Sélecteur en haut à droite (18 langues).',
          en: 'Selector at the top right (18 languages).',
          es: 'Selector en la parte superior derecha (18 idiomas).',
          'pt-BR': 'Seletor no canto superior direito (18 idiomas).',
          ar: 'المُحدِّد في أعلى اليمين (18 لغة).',
          'ar-SY': 'المُحدِّد بأعلى اليمين (18 لغة).',
          zh: '右上角的选择器（18 种语言）。',
        },
        steps: {
          fr: ['Cliquez sur le drapeau, choisissez votre langue. Le site et votre espace client s’affichent dans cette langue.'],
          en: ['Click the flag, pick your language. The site and your client area switch to it.'],
          es: ['Haga clic en la bandera y elija su idioma. El sitio y su espacio de cliente se mostrarán en ese idioma.'],
          'pt-BR': ['Clique na bandeira e escolha seu idioma. O site e sua área do cliente passam a ser exibidos nesse idioma.'],
          ar: ['انقر على العلم واختر لغتك. يُعرض الموقع ومساحة العميل الخاصة بك بهذه اللغة.'],
          'ar-SY': ['دوس عالعلم واختار لغتك. الموقع ومساحتك كزبون رح يبيّنوا بهاللغة.'],
          zh: ['点击旗帜，选择您的语言。网站和您的客户专区将以该语言显示。'],
        },
        can: {
          fr: ['Choisir parmi 18 langues (français, créole, portugais, anglais, espagnol…).'],
          en: ['Choose from 18 languages (French, Creole, Portuguese, English, Spanish…).'],
          es: ['Elegir entre 18 idiomas (francés, criollo, portugués, inglés, español…).'],
          'pt-BR': ['Escolher entre 18 idiomas (francês, crioulo, português, inglês, espanhol…).'],
          ar: ['الاختيار من بين 18 لغة (الفرنسية، الكريول، البرتغالية، الإنجليزية، الإسبانية…).'],
          'ar-SY': ['تختار من بين 18 لغة (فرنسي، كريول، برتغالي، إنكليزي، إسباني…).'],
          zh: ['可从 18 种语言中选择（法语、克里奥尔语、葡萄牙语、英语、西班牙语……）。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'suivi',
        icon: 'Search',
        title: {
          fr: 'Suivre ma réparation',
          en: 'Track my repair',
          es: 'Seguir mi reparación',
          'pt-BR': 'Acompanhar meu reparo',
          ar: 'تتبّع إصلاحي',
          'ar-SY': 'تتبّع تصليحي',
          zh: '追踪我的维修',
        },
        where: {
          fr: "Bloc « Suivre ma réparation » sur l'accueil.",
          en: 'The “Track my repair” block on the homepage.',
          es: 'Bloque «Seguir mi reparación» en la página de inicio.',
          'pt-BR': 'Bloco «Acompanhar meu reparo» na página inicial.',
          ar: 'مربّع «تتبّع إصلاحي» في الصفحة الرئيسية.',
          'ar-SY': 'مربّع «تتبّع تصليحي» بالصفحة الرئيسية.',
          zh: '首页上的“追踪我的维修”区块。',
        },
        steps: {
          fr: [
            'Entrez votre référence au format REM0001 (elle est sur votre ticket de dépôt) puis « Suivre ».',
            'Ou cliquez « Scanner le QR code de mon ticket » pour la lire avec l’appareil photo.',
            'Vous voyez le statut, l’appareil, les dates et le prix ; « Voir le détail complet » ouvre la page de suivi.',
          ],
          en: [
            'Enter your reference in the REM0001 format (on your drop-off ticket) then “Track”.',
            'Or tap “Scan my ticket QR code” to read it with the camera.',
            'You see the status, device, dates and price; “See full details” opens the tracking page.',
          ],
          es: [
            'Introduzca su referencia en formato REM0001 (está en su ticket de depósito) y pulse «Seguir».',
            'O haga clic en «Escanear el código QR de mi ticket» para leerlo con la cámara.',
            'Verá el estado, el dispositivo, las fechas y el precio; «Ver el detalle completo» abre la página de seguimiento.',
          ],
          'pt-BR': [
            'Digite sua referência no formato REM0001 (está no seu comprovante de entrega) e toque em «Acompanhar».',
            'Ou clique em «Escanear o QR code do meu comprovante» para lê-lo com a câmera.',
            'Você vê o status, o aparelho, as datas e o preço; «Ver detalhes completos» abre a página de acompanhamento.',
          ],
          ar: [
            'أدخِل رقمك المرجعي بصيغة REM0001 (موجود على إيصال الإيداع) ثم «تتبّع».',
            'أو انقر على «مسح رمز QR الخاص بإيصالي» لقراءته بالكاميرا.',
            'سترى الحالة والجهاز والتواريخ والسعر؛ «عرض التفاصيل الكاملة» يفتح صفحة التتبّع.',
          ],
          'ar-SY': [
            'دخّل الرقم المرجعي تبعك بصيغة REM0001 (موجود ع إيصال الإيداع) وبعدين «تتبّع».',
            'أو دوس «إمسح رمز QR تبع إيصالي» لتقراه بالكاميرا.',
            'رح تشوف الحالة والجهاز والتواريخ والسعر؛ «عرض كل التفاصيل» بيفتح صفحة التتبّع.',
          ],
          zh: [
            '按 REM0001 格式输入您的参考号（在您的寄存凭条上），然后点击“追踪”。',
            '或点击“扫描我凭条上的二维码”，用相机读取。',
            '您可看到状态、设备、日期和价格；“查看完整详情”会打开追踪页面。',
          ],
        },
        can: {
          fr: ['Suivre sans compte, avec la référence ou le QR code.'],
          en: ['Track without an account, using the reference or QR code.'],
          es: ['Hacer el seguimiento sin cuenta, con la referencia o el código QR.'],
          'pt-BR': ['Acompanhar sem conta, com a referência ou o QR code.'],
          ar: ['التتبّع بدون حساب، باستخدام الرقم المرجعي أو رمز QR.'],
          'ar-SY': ['تتبّع بدون حساب، بالرقم المرجعي أو رمز QR.'],
          zh: ['无需账户，用参考号或二维码即可追踪。'],
        },
        cannot: {
          fr: ['Voir des données personnelles : le suivi public n’affiche que le modèle, le statut et les dates (RGPD).'],
          en: ['See personal data: public tracking shows only model, status and dates (GDPR).'],
          es: ['Ver datos personales: el seguimiento público solo muestra el modelo, el estado y las fechas (RGPD).'],
          'pt-BR': ['Ver dados pessoais: o acompanhamento público mostra apenas o modelo, o status e as datas (RGPD).'],
          ar: ['رؤية البيانات الشخصية: التتبّع العام يعرض فقط الطراز والحالة والتواريخ (RGPD).'],
          'ar-SY': ['تشوف البيانات الشخصية: التتبّع العام بس بيبيّن الموديل والحالة والتواريخ (RGPD).'],
          zh: ['查看个人数据：公开追踪仅显示型号、状态和日期（RGPD）。'],
        },
      },
      {
        id: 'espaces',
        icon: 'LogIn',
        title: {
          fr: 'Les trois espaces',
          en: 'The three areas',
          es: 'Los tres espacios',
          'pt-BR': 'As três áreas',
          ar: 'المساحات الثلاث',
          'ar-SY': 'المساحات التلاتة',
          zh: '三个专区',
        },
        where: {
          fr: 'Boutons « Espace Client / Staff / Entreprise ».',
          en: 'The “Client / Staff / Business” buttons.',
          es: 'Botones «Espacio Cliente / Staff / Empresa».',
          'pt-BR': 'Botões «Área do Cliente / Staff / Empresa».',
          ar: 'أزرار «مساحة العميل / الفريق / الشركات».',
          'ar-SY': 'أزرار «مساحة الزبون / الفريق / الشركات».',
          zh: '“客户专区 / 员工 / 企业”按钮。',
        },
        steps: {
          fr: ['« Espace Client » mène à votre connexion ; « Staff » est réservé à l’équipe ; « Entreprise » au portail B2B.'],
          en: ['“Client area” leads to your login; “Staff” is for the team; “Business” is the B2B portal.'],
          es: ['«Espacio Cliente» lleva a su inicio de sesión; «Staff» está reservado al equipo; «Empresa» al portal B2B.'],
          'pt-BR': ['«Área do Cliente» leva ao seu login; «Staff» é reservado à equipe; «Empresa» ao portal B2B.'],
          ar: ['«مساحة العميل» تقودك إلى تسجيل الدخول؛ «الفريق» مخصّصة للموظّفين؛ «الشركات» بوّابة B2B.'],
          'ar-SY': ['«مساحة الزبون» بتوديك ع تسجيل الدخول؛ «الفريق» مخصّصة للموظّفين؛ «الشركات» بوّابة B2B.'],
          zh: ['“客户专区”通向您的登录；“员工”专供团队使用；“企业”是 B2B 门户。'],
        },
        can: {
          fr: ['Aller vers votre Espace Client.'],
          en: ['Go to your Client area.'],
          es: ['Ir a su Espacio Cliente.'],
          'pt-BR': ['Ir para sua Área do Cliente.'],
          ar: ['الانتقال إلى مساحة العميل الخاصة بك.'],
          'ar-SY': ['تروح ع مساحة الزبون تبعتك.'],
          zh: ['前往您的客户专区。'],
        },
        cannot: {
          fr: ['Entrer dans l’espace Staff ou Entreprise avec un compte client.'],
          en: ['Enter the Staff or Business area with a client account.'],
          es: ['Entrar en el espacio Staff o Empresa con una cuenta de cliente.'],
          'pt-BR': ['Entrar na área Staff ou Empresa com uma conta de cliente.'],
          ar: ['الدخول إلى مساحة الفريق أو الشركات بحساب عميل.'],
          'ar-SY': ['تفوت ع مساحة الفريق أو الشركات بحساب زبون.'],
          zh: ['用客户账户进入员工或企业专区。'],
        },
      },
      {
        id: 'cadbot',
        icon: 'Bot',
        title: {
          fr: 'CADBOT, l’assistant',
          en: 'CADBOT, the assistant',
          es: 'CADBOT, el asistente',
          'pt-BR': 'CADBOT, o assistente',
          ar: 'CADBOT، المساعد',
          'ar-SY': 'CADBOT، المساعد',
          zh: 'CADBOT 助手',
        },
        where: {
          fr: 'Le chat « CADBOT » sur l’accueil.',
          en: 'The “CADBOT” chat on the homepage.',
          es: 'El chat «CADBOT» en la página de inicio.',
          'pt-BR': 'O chat «CADBOT» na página inicial.',
          ar: 'محادثة «CADBOT» في الصفحة الرئيسية.',
          'ar-SY': 'محادثة «CADBOT» بالصفحة الرئيسية.',
          zh: '首页上的“CADBOT”聊天。',
        },
        steps: {
          fr: [
            'Posez une question : suivi (REM0001), estimation (« écran iPhone 11 cassé, combien ? »), horaires, adresse.',
            'Pour une estimation, CADBOT demande votre nom, votre téléphone et votre e-mail — obligatoires pour préparer et vous envoyer le devis.',
            'Vous pouvez « Reprendre ma conversation » ou en « Nouvelle conversation ».',
          ],
          en: [
            'Ask anything: tracking (REM0001), an estimate (“iPhone 11 screen, how much?”), hours, address.',
            'For an estimate, CADBOT asks your name, phone and e-mail — required to prepare and send your quote.',
            'You can “Resume my conversation” or start a “New conversation”.',
          ],
          es: [
            'Haga una pregunta: seguimiento (REM0001), estimación («pantalla de iPhone 11 rota, ¿cuánto?»), horarios, dirección.',
            'Para una estimación, CADBOT pide su nombre, su teléfono y su correo electrónico — obligatorios para preparar y enviarle el presupuesto.',
            'Puede «Retomar mi conversación» o iniciar una «Nueva conversación».',
          ],
          'pt-BR': [
            'Faça uma pergunta: acompanhamento (REM0001), estimativa («tela de iPhone 11 quebrada, quanto custa?»), horários, endereço.',
            'Para uma estimativa, o CADBOT pede seu nome, telefone e e-mail — obrigatórios para preparar e enviar o orçamento.',
            'Você pode «Retomar minha conversa» ou iniciar uma «Nova conversa».',
          ],
          ar: [
            'اطرح سؤالًا: تتبّع (REM0001)، تقدير سعر («شاشة iPhone 11 مكسورة، كم التكلفة؟»)، مواعيد العمل، العنوان.',
            'للحصول على تقدير، يطلب CADBOT اسمك وهاتفك وبريدك الإلكتروني — وهي إلزامية لإعداد عرض السعر وإرساله إليك.',
            'يمكنك «استئناف محادثتي» أو بدء «محادثة جديدة».',
          ],
          'ar-SY': [
            'إسأل سؤال: تتبّع (REM0001)، تقدير سعر («شاشة iPhone 11 مكسورة، قدّيش؟»)، مواعيد الدوام، العنوان.',
            'لتقدير السعر، CADBOT بيطلب اسمك وتلفونك وإيميلك — ضروريين لتجهيز عرض السعر وإرسالو إلك.',
            'فيك «ترجع لمحادثتي» أو تبلّش «محادثة جديدة».',
          ],
          zh: [
            '提出问题：追踪（REM0001）、估价（“iPhone 11 屏幕碎了，多少钱？”）、营业时间、地址。',
            '要估价时，CADBOT 会索取您的姓名、电话和电子邮箱——这是准备并向您发送报价所必需的。',
            '您可以“继续我的对话”或开始“新对话”。',
          ],
        },
        can: {
          fr: ['Obtenir une estimation, un suivi et les infos pratiques 24h/24.'],
          en: ['Get an estimate, tracking and practical info 24/7.'],
          es: ['Obtener una estimación, seguimiento e información práctica 24/7.'],
          'pt-BR': ['Obter uma estimativa, acompanhamento e informações práticas 24 horas por dia.'],
          ar: ['الحصول على تقدير وتتبّع ومعلومات عملية على مدار الساعة.'],
          'ar-SY': ['تاخد تقدير وتتبّع ومعلومات عملية 24 ساعة عاليوم.'],
          zh: ['全天候获取估价、追踪和实用信息。'],
        },
        cannot: {
          fr: [
            'Obtenir un devis sans donner nom + téléphone + e-mail.',
            'Considérer l’estimation comme définitive : le prix est toujours confirmé en boutique et le devis détaillé est envoyé par e-mail après validation de l’équipe.',
          ],
          en: [
            'Get a quote without giving name + phone + e-mail.',
            'Treat the estimate as final: the price is always confirmed in store and the detailed quote is e-mailed after the team validates it.',
          ],
          es: [
            'Obtener un presupuesto sin dar nombre + teléfono + correo electrónico.',
            'Considerar la estimación como definitiva: el precio siempre se confirma en la tienda y el presupuesto detallado se envía por correo electrónico tras la validación del equipo.',
          ],
          'pt-BR': [
            'Obter um orçamento sem informar nome + telefone + e-mail.',
            'Considerar a estimativa como definitiva: o preço é sempre confirmado na loja e o orçamento detalhado é enviado por e-mail após a validação da equipe.',
          ],
          ar: [
            'الحصول على عرض سعر دون تقديم الاسم + الهاتف + البريد الإلكتروني.',
            'اعتبار التقدير نهائيًا: يُؤكَّد السعر دائمًا في المتجر ويُرسَل عرض السعر المفصّل بالبريد الإلكتروني بعد مصادقة الفريق.',
          ],
          'ar-SY': [
            'تاخد عرض سعر بدون ما تعطي الاسم + التلفون + الإيميل.',
            'تعتبر التقدير نهائي: السعر دايمًا بيتأكّد بالمحل وعرض السعر المفصّل بيوصلك عالإيميل بعد ما يوافق الفريق.',
          ],
          zh: [
            '不提供姓名 + 电话 + 电子邮箱就获取报价。',
            '把估价当作最终价格：价格始终在门店确认，详细报价会在团队核实后通过电子邮件发送。',
          ],
        },
      },
      {
        id: 'services',
        icon: 'Wrench',
        title: {
          fr: 'Nos services',
          en: 'Our services',
          es: 'Nuestros servicios',
          'pt-BR': 'Nossos serviços',
          ar: 'خدماتنا',
          'ar-SY': 'خدماتنا',
          zh: '我们的服务',
        },
        where: {
          fr: 'Section « Nos Services de Réparation ».',
          en: 'The “Our repair services” section.',
          es: 'Sección «Nuestros servicios de reparación».',
          'pt-BR': 'Seção «Nossos serviços de reparo».',
          ar: 'قسم «خدمات الإصلاح لدينا».',
          'ar-SY': 'قسم «خدمات التصليح تبعنا».',
          zh: '“我们的维修服务”版块。',
        },
        steps: {
          fr: ['Écran, batterie, connecteur de charge, caméra, désoxydation, diagnostic. « Voir tous nos tarifs » ouvre la grille des prix.'],
          en: ['Screen, battery, charging port, camera, water-damage, diagnostic. “See all prices” opens the price list.'],
          es: ['Pantalla, batería, conector de carga, cámara, tratamiento antihumedad, diagnóstico. «Ver todos nuestros precios» abre la lista de precios.'],
          'pt-BR': ['Tela, bateria, conector de carga, câmera, tratamento contra oxidação, diagnóstico. «Ver todos os preços» abre a tabela de preços.'],
          ar: ['الشاشة، البطارية، منفذ الشحن، الكاميرا، معالجة آثار السوائل، التشخيص. «عرض جميع الأسعار» يفتح قائمة الأسعار.'],
          'ar-SY': ['الشاشة، البطارية، مدخل الشحن، الكاميرا، معالجة آثار المي، الفحص. «عرض كل الأسعار» بيفتح لائحة الأسعار.'],
          zh: ['屏幕、电池、充电接口、摄像头、进水处理、检测。“查看全部价格”会打开价目表。'],
        },
        can: {
          fr: ['Découvrir les réparations proposées.'],
          en: ['Discover the repairs offered.'],
          es: ['Descubrir las reparaciones ofrecidas.'],
          'pt-BR': ['Descobrir os reparos oferecidos.'],
          ar: ['التعرّف على خدمات الإصلاح المتاحة.'],
          'ar-SY': ['تتعرّف عالتصليحات المتوفّرة.'],
          zh: ['了解所提供的维修服务。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'tarifs',
        icon: 'Tag',
        title: {
          fr: 'Les tarifs',
          en: 'The price list',
          es: 'Las tarifas',
          'pt-BR': 'A tabela de preços',
          ar: 'قائمة الأسعار',
          'ar-SY': 'لائحة الأسعار',
          zh: '价目表',
        },
        where: {
          fr: 'Page « Tarifs » (bouton « Voir tous nos tarifs »).',
          en: 'The “Prices” page (“See all prices”).',
          es: 'Página «Tarifas» (botón «Ver todos nuestros precios»).',
          'pt-BR': 'Página «Preços» (botão «Ver todos os preços»).',
          ar: 'صفحة «الأسعار» (زر «عرض جميع الأسعار»).',
          'ar-SY': 'صفحة «الأسعار» (زر «عرض كل الأسعار»).',
          zh: '“价格”页面（“查看全部价格”按钮）。',
        },
        steps: {
          fr: ['Cherchez votre modèle, dépliez la marque pour voir les fourchettes de prix par réparation.'],
          en: ['Search your model, expand the brand to see price ranges per repair.'],
          es: ['Busque su modelo y despliegue la marca para ver los rangos de precios por reparación.'],
          'pt-BR': ['Busque seu modelo e expanda a marca para ver as faixas de preço por reparo.'],
          ar: ['ابحث عن طرازك ووسّع العلامة التجارية لرؤية نطاقات الأسعار لكل إصلاح.'],
          'ar-SY': ['دوّر ع موديلك وافتح الماركة لتشوف مجالات الأسعار لكل تصليح.'],
          zh: ['搜索您的型号，展开品牌即可查看各项维修的价格区间。'],
        },
        can: {
          fr: ['Consulter les prix publics indicatifs.'],
          en: ['Check indicative public prices.'],
          es: ['Consultar los precios públicos orientativos.'],
          'pt-BR': ['Consultar os preços públicos indicativos.'],
          ar: ['الاطّلاع على الأسعار العامة الاسترشادية.'],
          'ar-SY': ['تطّلع عالأسعار العامة الاسترشادية.'],
          zh: ['查看公开的参考价格。'],
        },
        cannot: {
          fr: ['Voir les prix d’achat / coûts internes : jamais affichés.'],
          en: ['See purchase/cost prices: never shown.'],
          es: ['Ver los precios de compra / costes internos: nunca se muestran.'],
          'pt-BR': ['Ver os preços de compra / custos internos: nunca são exibidos.'],
          ar: ['رؤية أسعار الشراء / التكاليف الداخلية: لا تُعرَض أبدًا.'],
          'ar-SY': ['تشوف أسعار الشرا / التكاليف الداخلية: ما بتنعرض أبدًا.'],
          zh: ['查看进货价 / 内部成本：从不显示。'],
        },
      },
      {
        id: 'diagnostic',
        icon: 'Stethoscope',
        title: {
          fr: 'Le diagnostic',
          en: 'The diagnostic',
          es: 'El diagnóstico',
          'pt-BR': 'O diagnóstico',
          ar: 'التشخيص',
          'ar-SY': 'الفحص',
          zh: '检测',
        },
        where: {
          fr: 'Section « Diagnostic en boutique ».',
          en: 'The “In-store diagnostic” section.',
          es: 'Sección «Diagnóstico en tienda».',
          'pt-BR': 'Seção «Diagnóstico na loja».',
          ar: 'قسم «التشخيص في المتجر».',
          'ar-SY': 'قسم «الفحص بالمحل».',
          zh: '“门店检测”版块。',
        },
        steps: {
          fr: ['De 35 à 80 € selon l’appareil ; le tarif est annoncé avant de commencer. Le devis en ligne, lui, est gratuit.'],
          en: ['From €35 to €80 depending on the device; the price is announced before starting. The online quote is free.'],
          es: ['De 35 a 80 € según el dispositivo; la tarifa se anuncia antes de empezar. El presupuesto en línea, en cambio, es gratuito.'],
          'pt-BR': ['De 35 a 80 € conforme o aparelho; o valor é informado antes de começar. Já o orçamento on-line é gratuito.'],
          ar: ['من 35 إلى 80 € حسب الجهاز؛ يُعلَن السعر قبل البدء. أمّا عرض السعر عبر الإنترنت فهو مجّاني.'],
          'ar-SY': ['من 35 لـ 80 € حسب الجهاز؛ السعر بينقال قبل ما يبلّشوا. أمّا عرض السعر أونلاين فهو مجّاني.'],
          zh: ['根据设备从 35 到 80 欧元不等；开始前会告知价格。而在线报价是免费的。'],
        },
        can: {
          fr: ['Connaître le coût du diagnostic à l’avance.'],
          en: ['Know the diagnostic cost in advance.'],
          es: ['Conocer el coste del diagnóstico por adelantado.'],
          'pt-BR': ['Saber o custo do diagnóstico com antecedência.'],
          ar: ['معرفة تكلفة التشخيص مسبقًا.'],
          'ar-SY': ['تعرف كلفة الفحص من قبل.'],
          zh: ['提前了解检测费用。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'avis',
        icon: 'Star',
        title: {
          fr: 'Avis Google',
          en: 'Google reviews',
          es: 'Reseñas de Google',
          'pt-BR': 'Avaliações do Google',
          ar: 'تقييمات Google',
          'ar-SY': 'تقييمات Google',
          zh: 'Google 评价',
        },
        where: {
          fr: 'Section « Nos clients nous évaluent sur Google ».',
          en: 'The “Our customers rate us on Google” section.',
          es: 'Sección «Nuestros clientes nos valoran en Google».',
          'pt-BR': 'Seção «Nossos clientes nos avaliam no Google».',
          ar: 'قسم «عملاؤنا يقيّموننا على Google».',
          'ar-SY': 'قسم «زباينّا بيقيّمونا ع Google».',
          zh: '“我们的客户在 Google 上评价我们”版块。',
        },
        steps: {
          fr: ['Lisez les avis et laissez le vôtre via « Lire nos avis sur Google ».'],
          en: ['Read reviews and leave yours via “Read our Google reviews”.'],
          es: ['Lea las reseñas y deje la suya en «Leer nuestras reseñas en Google».'],
          'pt-BR': ['Leia as avaliações e deixe a sua em «Ler nossas avaliações no Google».'],
          ar: ['اقرأ التقييمات واترك تقييمك عبر «اقرأ تقييماتنا على Google».'],
          'ar-SY': ['إقرا التقييمات واترك تقييمك من «إقرا تقييماتنا ع Google».'],
          zh: ['通过“阅读我们的 Google 评价”阅读评价并留下您的评价。'],
        },
        can: {
          fr: ['Consulter et laisser un avis.'],
          en: ['Read and leave a review.'],
          es: ['Consultar y dejar una reseña.'],
          'pt-BR': ['Consultar e deixar uma avaliação.'],
          ar: ['الاطّلاع على التقييمات وترك تقييم.'],
          'ar-SY': ['تطّلع وتترك تقييم.'],
          zh: ['查看并留下评价。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'contact',
        icon: 'Phone',
        title: {
          fr: 'Contact & WhatsApp',
          en: 'Contact & WhatsApp',
          es: 'Contacto y WhatsApp',
          'pt-BR': 'Contato e WhatsApp',
          ar: 'التواصل وWhatsApp',
          'ar-SY': 'التواصل وWhatsApp',
          zh: '联系方式与 WhatsApp',
        },
        where: {
          fr: 'Section « Contact ».',
          en: 'The “Contact” section.',
          es: 'Sección «Contacto».',
          'pt-BR': 'Seção «Contato».',
          ar: 'قسم «التواصل».',
          'ar-SY': 'قسم «التواصل».',
          zh: '“联系方式”版块。',
        },
        steps: {
          fr: ['Appelez ou écrivez sur WhatsApp au 0694 27 40 51 (mêmes numéro).'],
          en: ['Call or message on WhatsApp at 0694 27 40 51 (same number).'],
          es: ['Llame o escriba por WhatsApp al 0694 27 40 51 (mismo número).'],
          'pt-BR': ['Ligue ou escreva no WhatsApp para 0694 27 40 51 (mesmo número).'],
          ar: ['اتّصل أو راسِل عبر WhatsApp على الرقم 0694 27 40 51 (نفس الرقم).'],
          'ar-SY': ['اتّصل أو راسل ع WhatsApp عالرقم 0694 27 40 51 (نفس الرقم).'],
          zh: ['拨打电话或通过 WhatsApp 发送消息至 0694 27 40 51（同一号码）。'],
        },
        can: {
          fr: ['Joindre la boutique directement.'],
          en: ['Reach the shop directly.'],
          es: ['Contactar directamente con la tienda.'],
          'pt-BR': ['Falar diretamente com a loja.'],
          ar: ['التواصل مع المتجر مباشرةً.'],
          'ar-SY': ['تتواصل مع المحل مباشرةً.'],
          zh: ['直接联系门店。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'horaires',
        icon: 'Clock',
        title: {
          fr: 'Horaires en direct',
          en: 'Live opening hours',
          es: 'Horario en directo',
          'pt-BR': 'Horário em tempo real',
          ar: 'مواعيد العمل مباشرةً',
          'ar-SY': 'مواعيد الدوام مباشرةً',
          zh: '实时营业时间',
        },
        where: {
          fr: 'Section « Horaires d’ouverture ».',
          en: 'The “Opening hours” section.',
          es: 'Sección «Horario de apertura».',
          'pt-BR': 'Seção «Horário de funcionamento».',
          ar: 'قسم «مواعيد العمل».',
          'ar-SY': 'قسم «مواعيد الدوام».',
          zh: '“营业时间”版块。',
        },
        steps: {
          fr: ['L’anneau animé indique, à l’heure de Cayenne, si c’est ouvert et le temps restant. Lun–Ven 9h–19h, Sam 9h–14h, Dim fermé.'],
          en: ['The animated ring shows, in Cayenne time, whether it’s open and the time left. Mon–Fri 9am–7pm, Sat 9am–2pm, Sun closed.'],
          es: ['El anillo animado indica, en hora de Cayena, si está abierto y el tiempo restante. Lun–Vie 9:00–19:00, Sáb 9:00–14:00, Dom cerrado.'],
          'pt-BR': ['O anel animado indica, no horário de Caiena, se está aberto e o tempo restante. Seg–Sex 9h–19h, Sáb 9h–14h, Dom fechado.'],
          ar: ['تُظهِر الحلقة المتحرّكة، بتوقيت كايين، ما إذا كان المتجر مفتوحًا والوقت المتبقّي. الاثنين–الجمعة 9–19، السبت 9–14، الأحد مغلق.'],
          'ar-SY': ['الحلقة المتحرّكة بتبيّن، بتوقيت كايين، إذا المحل مفتوح وقدّيش باقي وقت. التنين–الجمعة 9–19، السبت 9–14، الأحد مسكّر.'],
          zh: ['动态圆环以卡宴时间显示是否营业及剩余时间。周一至周五 9:00–19:00，周六 9:00–14:00，周日休息。'],
        },
        can: {
          fr: ['Savoir en un coup d’œil si la boutique est ouverte.'],
          en: ['See at a glance whether the shop is open.'],
          es: ['Saber de un vistazo si la tienda está abierta.'],
          'pt-BR': ['Saber num relance se a loja está aberta.'],
          ar: ['معرفة ما إذا كان المتجر مفتوحًا بنظرة واحدة.'],
          'ar-SY': ['تعرف بلمحة إذا المحل مفتوح.'],
          zh: ['一眼看出门店是否营业。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'legal',
        icon: 'MapPin',
        title: {
          fr: 'Adresse & infos légales',
          en: 'Address & legal info',
          es: 'Dirección e información legal',
          'pt-BR': 'Endereço e informações legais',
          ar: 'العنوان والمعلومات القانونية',
          'ar-SY': 'العنوان والمعلومات القانونية',
          zh: '地址与法律信息',
        },
        where: {
          fr: 'Bas de page.',
          en: 'Footer.',
          es: 'Pie de página.',
          'pt-BR': 'Rodapé.',
          ar: 'تذييل الصفحة.',
          'ar-SY': 'آخر الصفحة.',
          zh: '页脚。',
        },
        steps: {
          fr: ['Adresse de la boutique, « Ouvrir dans Maps », et les mentions légales de la société.'],
          en: ['Shop address, “Open in Maps”, and the company’s legal notices.'],
          es: ['Dirección de la tienda, «Abrir en Maps» y los avisos legales de la empresa.'],
          'pt-BR': ['Endereço da loja, «Abrir no Maps» e os avisos legais da empresa.'],
          ar: ['عنوان المتجر، و«الفتح في Maps»، والإشعارات القانونية للشركة.'],
          'ar-SY': ['عنوان المحل، و«افتح بـ Maps»، والمعلومات القانونية للشركة.'],
          zh: ['门店地址、“在 Maps 中打开”以及公司的法律声明。'],
        },
        can: {
          fr: ['Ouvrir l’itinéraire vers la boutique.'],
          en: ['Open directions to the shop.'],
          es: ['Abrir la ruta hacia la tienda.'],
          'pt-BR': ['Abrir a rota até a loja.'],
          ar: ['فتح الاتجاهات إلى المتجر.'],
          'ar-SY': ['تفتح الطريق عالمحل.'],
          zh: ['打开前往门店的路线。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'devis-gratuit',
        icon: 'FileText',
        title: {
          fr: 'Bouton « Devis gratuit »',
          en: '“Free quote” button',
          es: 'Botón «Presupuesto gratis»',
          'pt-BR': 'Botão «Orçamento grátis»',
          ar: 'زر «عرض سعر مجّاني»',
          'ar-SY': 'زر «عرض سعر مجّاني»',
          zh: '“免费报价”按钮',
        },
        where: {
          fr: 'Bouton flottant en bas à droite.',
          en: 'Floating button, bottom right.',
          es: 'Botón flotante abajo a la derecha.',
          'pt-BR': 'Botão flutuante no canto inferior direito.',
          ar: 'زر عائم في أسفل اليمين.',
          'ar-SY': 'زر عايم بأسفل اليمين.',
          zh: '右下角的浮动按钮。',
        },
        steps: {
          fr: ['Il vous amène directement au chat CADBOT pour lancer une estimation.'],
          en: ['It takes you straight to the CADBOT chat to start an estimate.'],
          es: ['Le lleva directamente al chat CADBOT para iniciar una estimación.'],
          'pt-BR': ['Leva você direto ao chat CADBOT para iniciar uma estimativa.'],
          ar: ['ينقلك مباشرةً إلى محادثة CADBOT لبدء تقدير السعر.'],
          'ar-SY': ['بياخدك دغري ع محادثة CADBOT لتبلّش تقدير سعر.'],
          zh: ['它会直接带您进入 CADBOT 聊天以开始估价。'],
        },
        can: {
          fr: ['Démarrer un devis en un clic.'],
          en: ['Start a quote in one click.'],
          es: ['Iniciar un presupuesto con un clic.'],
          'pt-BR': ['Iniciar um orçamento com um clique.'],
          ar: ['بدء عرض سعر بنقرة واحدة.'],
          'ar-SY': ['تبلّش عرض سعر بنقرة وحدة.'],
          zh: ['一键开始报价。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'suivi-public',
        icon: 'PackageSearch',
        title: {
          fr: 'Page de suivi public',
          en: 'Public tracking page',
          es: 'Página de seguimiento público',
          'pt-BR': 'Página de acompanhamento público',
          ar: 'صفحة التتبّع العامة',
          'ar-SY': 'صفحة التتبّع العامة',
          zh: '公开追踪页面',
        },
        where: {
          fr: 'Après avoir suivi une référence (/suivi/REM…).',
          en: 'After tracking a reference (/suivi/REM…).',
          es: 'Después de seguir una referencia (/suivi/REM…).',
          'pt-BR': 'Após acompanhar uma referência (/suivi/REM…).',
          ar: 'بعد تتبّع رقم مرجعي (/suivi/REM…).',
          'ar-SY': 'بعد ما تتبّع رقم مرجعي (/suivi/REM…).',
          zh: '追踪某个参考号后（/suivi/REM…）。',
        },
        steps: {
          fr: ['Statut détaillé, appareil, dates de dépôt/retour, prix, et « Contacter la boutique ». Une promo de retrait s’affiche quand la réparation est prête.'],
          en: ['Detailed status, device, drop-off/return dates, price, and “Contact the shop”. A pickup offer appears when the repair is ready.'],
          es: ['Estado detallado, dispositivo, fechas de depósito/retiro, precio y «Contactar con la tienda». Aparece una promoción de retiro cuando la reparación está lista.'],
          'pt-BR': ['Status detalhado, aparelho, datas de entrega/retirada, preço e «Contatar a loja». Uma promoção de retirada aparece quando o reparo está pronto.'],
          ar: ['الحالة المفصّلة، الجهاز، تواريخ الإيداع/الاستلام، السعر، و«التواصل مع المتجر». يظهر عرض ترويجي للاستلام عندما يصبح الإصلاح جاهزًا.'],
          'ar-SY': ['الحالة المفصّلة، الجهاز، تواريخ الإيداع/الاستلام، السعر، و«تواصل مع المحل». بيطلع عرض للاستلام لمّا يصير التصليح جاهز.'],
          zh: ['详细状态、设备、寄存/取回日期、价格以及“联系门店”。维修完成时会显示取件优惠。'],
        },
        can: {
          fr: ['Suivre en détail sans compte.'],
          en: ['Track in detail without an account.'],
          es: ['Hacer un seguimiento detallado sin cuenta.'],
          'pt-BR': ['Acompanhar em detalhe sem conta.'],
          ar: ['التتبّع بالتفصيل دون حساب.'],
          'ar-SY': ['تتبّع بالتفصيل بدون حساب.'],
          zh: ['无需账户即可详细追踪。'],
        },
        cannot: {
          fr: ['Voir des données personnelles (protégées).'],
          en: ['See personal data (protected).'],
          es: ['Ver datos personales (protegidos).'],
          'pt-BR': ['Ver dados pessoais (protegidos).'],
          ar: ['رؤية البيانات الشخصية (محميّة).'],
          'ar-SY': ['تشوف البيانات الشخصية (محميّة).'],
          zh: ['查看个人数据（受保护）。'],
        },
      },
    ],
  },

  // ─────────────────────────────── B. COMPTE ───────────────────────────────
  {
    id: 'compte',
    icon: 'UserCircle',
    title: {
      fr: 'Votre compte',
      en: 'Your account',
      es: 'Su cuenta',
      'pt-BR': 'Sua conta',
      ar: 'حسابك',
      'ar-SY': 'حسابك',
      zh: '您的账户',
    },
    intro: {
      fr: 'Créer votre compte et vous connecter à votre espace client.',
      en: 'Create your account and sign in to your client area.',
      es: 'Cree su cuenta e inicie sesión en su espacio de cliente.',
      'pt-BR': 'Crie sua conta e faça login na sua área do cliente.',
      ar: 'أنشئ حسابك وسجّل الدخول إلى مساحة العميل الخاصة بك.',
      'ar-SY': 'إعمل حسابك وسجّل دخول ع مساحة الزبون تبعتك.',
      zh: '创建您的账户并登录客户专区。',
    },
    items: [
      {
        id: 'inscription',
        icon: 'UserPlus',
        title: {
          fr: 'Créer un compte',
          en: 'Create an account',
          es: 'Crear una cuenta',
          'pt-BR': 'Criar uma conta',
          ar: 'إنشاء حساب',
          'ar-SY': 'إنشاء حساب',
          zh: '创建账户',
        },
        where: {
          fr: '« Espace Client » puis « Créer un compte ».',
          en: '“Client area” then “Create an account”.',
          es: '«Espacio Cliente» y luego «Crear una cuenta».',
          'pt-BR': '«Área do Cliente» e depois «Criar uma conta».',
          ar: '«مساحة العميل» ثم «إنشاء حساب».',
          'ar-SY': '«مساحة الزبون» وبعدين «إنشاء حساب».',
          zh: '“客户专区”，然后“创建账户”。',
        },
        steps: {
          fr: [
            'Renseignez : nom complet, téléphone, e-mail (obligatoires), adresse (facultative), mot de passe, et un code de parrainage si vous en avez un.',
            'Un e-mail de confirmation est envoyé : cliquez le lien pour activer le compte.',
          ],
          en: [
            'Fill in: full name, phone, e-mail (required), address (optional), password, and a referral code if you have one.',
            'A confirmation e-mail is sent: click the link to activate the account.',
          ],
          es: [
            'Complete: nombre completo, teléfono, correo electrónico (obligatorios), dirección (opcional), contraseña y un código de patrocinio si tiene uno.',
            'Se envía un correo electrónico de confirmación: haga clic en el enlace para activar la cuenta.',
          ],
          'pt-BR': [
            'Preencha: nome completo, telefone, e-mail (obrigatórios), endereço (opcional), senha e um código de indicação, se tiver um.',
            'Um e-mail de confirmação é enviado: clique no link para ativar a conta.',
          ],
          ar: [
            'أدخِل: الاسم الكامل، الهاتف، البريد الإلكتروني (إلزامية)، العنوان (اختياري)، كلمة المرور، ورمز إحالة إن وُجد.',
            'يُرسَل بريد إلكتروني للتأكيد: انقر على الرابط لتفعيل الحساب.',
          ],
          'ar-SY': [
            'عبّي: الاسم الكامل، التلفون، الإيميل (ضروريين)، العنوان (اختياري)، كلمة السر، ورمز إحالة إذا عندك.',
            'بيوصلك إيميل تأكيد: دوس عالرابط لتفعّل الحساب.',
          ],
          zh: [
            '填写：全名、电话、电子邮箱（必填）、地址（选填）、密码，以及推荐码（如有）。',
            '系统会发送一封确认邮件：点击链接以激活账户。',
          ],
        },
        can: {
          fr: ['Créer votre compte et gagner un bonus si vous utilisez un code de parrainage.'],
          en: ['Create your account and earn a bonus with a referral code.'],
          es: ['Crear su cuenta y ganar un bono si usa un código de patrocinio.'],
          'pt-BR': ['Criar sua conta e ganhar um bônus se usar um código de indicação.'],
          ar: ['إنشاء حسابك والحصول على مكافأة إذا استخدمت رمز إحالة.'],
          'ar-SY': ['تعمل حسابك وتاخد مكافأة إذا استخدمت رمز إحالة.'],
          zh: ['创建您的账户，使用推荐码还可获得奖励。'],
        },
        cannot: {
          fr: ['Vous connecter avant d’avoir validé l’e-mail de confirmation.'],
          en: ['Sign in before confirming your e-mail.'],
          es: ['Iniciar sesión antes de validar el correo electrónico de confirmación.'],
          'pt-BR': ['Fazer login antes de confirmar o e-mail de confirmação.'],
          ar: ['تسجيل الدخول قبل تأكيد البريد الإلكتروني.'],
          'ar-SY': ['تسجّل دخول قبل ما تأكّد الإيميل.'],
          zh: ['在确认邮件验证之前登录。'],
        },
      },
      {
        id: 'connexion',
        icon: 'LogIn',
        title: {
          fr: 'Se connecter',
          en: 'Sign in',
          es: 'Iniciar sesión',
          'pt-BR': 'Fazer login',
          ar: 'تسجيل الدخول',
          'ar-SY': 'تسجيل الدخول',
          zh: '登录',
        },
        where: {
          fr: '« Espace Client ».',
          en: '“Client area”.',
          es: '«Espacio Cliente».',
          'pt-BR': '«Área do Cliente».',
          ar: '«مساحة العميل».',
          'ar-SY': '«مساحة الزبون».',
          zh: '“客户专区”。',
        },
        steps: {
          fr: ['Connectez-vous avec votre NUMÉRO DE TÉLÉPHONE et votre mot de passe.'],
          en: ['Sign in with your PHONE NUMBER and your password.'],
          es: ['Inicie sesión con su NÚMERO DE TELÉFONO y su contraseña.'],
          'pt-BR': ['Faça login com seu NÚMERO DE TELEFONE e sua senha.'],
          ar: ['سجّل الدخول باستخدام رقم هاتفك وكلمة المرور.'],
          'ar-SY': ['سجّل دخول برقم تلفونك وكلمة السر.'],
          zh: ['使用您的电话号码和密码登录。'],
        },
        can: {
          fr: ['Accéder à votre espace client.'],
          en: ['Access your client area.'],
          es: ['Acceder a su espacio de cliente.'],
          'pt-BR': ['Acessar sua área do cliente.'],
          ar: ['الوصول إلى مساحة العميل الخاصة بك.'],
          'ar-SY': ['تفوت ع مساحة الزبون تبعتك.'],
          zh: ['进入您的客户专区。'],
        },
        cannot: {
          fr: ['Utiliser un e-mail pour vous connecter : l’identifiant est le téléphone.'],
          en: ['Use an e-mail to sign in: the login is the phone number.'],
          es: ['Usar un correo electrónico para iniciar sesión: el identificador es el teléfono.'],
          'pt-BR': ['Usar um e-mail para fazer login: o identificador é o telefone.'],
          ar: ['استخدام البريد الإلكتروني لتسجيل الدخول: المعرّف هو رقم الهاتف.'],
          'ar-SY': ['تستخدم الإيميل لتسجيل الدخول: المعرّف هو رقم التلفون.'],
          zh: ['用电子邮箱登录：登录标识是电话号码。'],
        },
      },
      {
        id: 'mdp-oublie',
        icon: 'KeyRound',
        title: {
          fr: 'Mot de passe oublié',
          en: 'Forgot password',
          es: 'Contraseña olvidada',
          'pt-BR': 'Esqueci a senha',
          ar: 'نسيت كلمة المرور',
          'ar-SY': 'نسيت كلمة السر',
          zh: '忘记密码',
        },
        where: {
          fr: '« Mot de passe oublié ? » sous le formulaire.',
          en: '“Forgot password?” under the form.',
          es: '«¿Contraseña olvidada?» debajo del formulario.',
          'pt-BR': '«Esqueceu a senha?» abaixo do formulário.',
          ar: '«نسيت كلمة المرور؟» أسفل النموذج.',
          'ar-SY': '«نسيت كلمة السر؟» تحت الاستمارة.',
          zh: '表单下方的“忘记密码？”。',
        },
        steps: {
          fr: ['Choisissez « Client », entrez votre e-mail : un lien de réinitialisation vous est envoyé.'],
          en: ['Choose “Client”, enter your e-mail: a reset link is sent to you.'],
          es: ['Elija «Cliente», introduzca su correo electrónico: se le envía un enlace de restablecimiento.'],
          'pt-BR': ['Escolha «Cliente», digite seu e-mail: um link de redefinição é enviado a você.'],
          ar: ['اختر «عميل»، وأدخِل بريدك الإلكتروني: يُرسَل إليك رابط لإعادة التعيين.'],
          'ar-SY': ['اختار «زبون»، ودخّل إيميلك: بيوصلك رابط لإعادة تعيين كلمة السر.'],
          zh: ['选择“客户”，输入您的电子邮箱：系统会向您发送一个重置链接。'],
        },
        can: {
          fr: ['Réinitialiser votre mot de passe par e-mail.'],
          en: ['Reset your password by e-mail.'],
          es: ['Restablecer su contraseña por correo electrónico.'],
          'pt-BR': ['Redefinir sua senha por e-mail.'],
          ar: ['إعادة تعيين كلمة المرور عبر البريد الإلكتروني.'],
          'ar-SY': ['تعيّن كلمة السر من جديد عن طريق الإيميل.'],
          zh: ['通过电子邮箱重置您的密码。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'changer-mdp',
        icon: 'Lock',
        title: {
          fr: 'Changer mon mot de passe / code',
          en: 'Change my password / PIN',
          es: 'Cambiar mi contraseña / código',
          'pt-BR': 'Alterar minha senha / código',
          ar: 'تغيير كلمة المرور / الرمز',
          'ar-SY': 'تغيير كلمة السر / الرمز',
          zh: '更改我的密码 / PIN 码',
        },
        where: {
          fr: 'Espace client : « Mon compte » ou l’icône cadenas.',
          en: 'Client area: “My account” or the lock icon.',
          es: 'Espacio de cliente: «Mi cuenta» o el icono del candado.',
          'pt-BR': 'Área do cliente: «Minha conta» ou o ícone de cadeado.',
          ar: 'مساحة العميل: «حسابي» أو أيقونة القفل.',
          'ar-SY': 'مساحة الزبون: «حسابي» أو أيقونة القفل.',
          zh: '客户专区：“我的账户”或锁形图标。',
        },
        steps: {
          fr: ['Saisissez l’ancien puis le nouveau mot de passe. À la première connexion, un changement peut être demandé.'],
          en: ['Enter the old then the new password. On first login a change may be required.'],
          es: ['Introduzca la contraseña anterior y luego la nueva. En el primer inicio de sesión puede solicitarse un cambio.'],
          'pt-BR': ['Digite a senha antiga e depois a nova. No primeiro login, pode ser solicitada uma alteração.'],
          ar: ['أدخِل كلمة المرور القديمة ثم الجديدة. عند أول تسجيل دخول قد يُطلَب تغييرها.'],
          'ar-SY': ['دخّل كلمة السر القديمة وبعدين الجديدة. بأول تسجيل دخول ممكن يُطلب منك تغييرها.'],
          zh: ['先输入旧密码，再输入新密码。首次登录时可能需要更改。'],
        },
        can: {
          fr: ['Modifier votre mot de passe à tout moment.'],
          en: ['Change your password at any time.'],
          es: ['Cambiar su contraseña en cualquier momento.'],
          'pt-BR': ['Alterar sua senha a qualquer momento.'],
          ar: ['تغيير كلمة المرور في أي وقت.'],
          'ar-SY': ['تغيّر كلمة السر بأي وقت.'],
          zh: ['随时更改您的密码。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
    ],
  },

  // ─────────────────────────────── C. ESPACE CLIENT ───────────────────────────────
  {
    id: 'espace-client',
    icon: 'LayoutDashboard',
    title: {
      fr: 'Votre espace client',
      en: 'Your client area',
      es: 'Su espacio de cliente',
      'pt-BR': 'Sua área do cliente',
      ar: 'مساحة العميل الخاصة بك',
      'ar-SY': 'مساحة الزبون تبعتك',
      zh: '您的客户专区',
    },
    intro: {
      fr: 'Une fois connecté : réparations, boutique, commandes, devis, factures, adresses et chat.',
      en: 'Once signed in: repairs, shop, orders, quotes, invoices, addresses and chat.',
      es: 'Una vez conectado: reparaciones, tienda, pedidos, presupuestos, facturas, direcciones y chat.',
      'pt-BR': 'Depois de fazer login: reparos, loja, pedidos, orçamentos, faturas, endereços e chat.',
      ar: 'بعد تسجيل الدخول: الإصلاحات، المتجر، الطلبات، عروض الأسعار، الفواتير، العناوين والمحادثة.',
      'ar-SY': 'بعد ما تسجّل دخول: التصليحات، المتجر، الطلبات، عروض الأسعار، الفواتير، العناوين والمحادثة.',
      zh: '登录后：维修、商店、订单、报价、发票、地址和聊天。',
    },
    items: [
      {
        id: 'vue-ensemble',
        icon: 'LayoutDashboard',
        title: {
          fr: 'Vue d’ensemble',
          en: 'Overview',
          es: 'Vista general',
          'pt-BR': 'Visão geral',
          ar: 'نظرة عامة',
          'ar-SY': 'نظرة عامة',
          zh: '总览',
        },
        where: {
          fr: 'Barre latérale + barre du haut.',
          en: 'Sidebar + top bar.',
          es: 'Barra lateral + barra superior.',
          'pt-BR': 'Barra lateral + barra superior.',
          ar: 'الشريط الجانبي + الشريط العلوي.',
          'ar-SY': 'الشريط الجانبي + الشريط العلوي.',
          zh: '侧边栏 + 顶部栏。',
        },
        steps: {
          fr: ['Naviguez entre Réparations, Boutique, Commandes, Devis/Factures, Mes adresses. En haut : langue, cloche de notifications, déconnexion.'],
          en: ['Move between Repairs, Shop, Orders, Quotes/Invoices, My addresses. Top bar: language, notification bell, sign out.'],
          es: ['Navegue entre Reparaciones, Tienda, Pedidos, Presupuestos/Facturas, Mis direcciones. Arriba: idioma, campana de notificaciones, cierre de sesión.'],
          'pt-BR': ['Navegue entre Reparos, Loja, Pedidos, Orçamentos/Faturas, Meus endereços. No topo: idioma, sino de notificações, sair.'],
          ar: ['تنقّل بين الإصلاحات، المتجر، الطلبات، عروض الأسعار/الفواتير، عناويني. في الأعلى: اللغة، جرس الإشعارات، تسجيل الخروج.'],
          'ar-SY': ['تنقّل بين التصليحات، المتجر، الطلبات، عروض الأسعار/الفواتير، عناويني. بالأعلى: اللغة، جرس الإشعارات، تسجيل الخروج.'],
          zh: ['在维修、商店、订单、报价/发票、我的地址之间切换。顶部：语言、通知铃铛、退出登录。'],
        },
        can: {
          fr: ['Tout piloter depuis un seul écran, dans votre langue.'],
          en: ['Manage everything from one screen, in your language.'],
          es: ['Gestionar todo desde una sola pantalla, en su idioma.'],
          'pt-BR': ['Gerenciar tudo em uma única tela, no seu idioma.'],
          ar: ['إدارة كل شيء من شاشة واحدة، بلغتك.'],
          'ar-SY': ['تدير كل شي من شاشة وحدة، بلغتك.'],
          zh: ['在单个屏幕上用您的语言管理一切。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'reparations',
        icon: 'Wrench',
        title: {
          fr: 'Mes réparations',
          en: 'My repairs',
          es: 'Mis reparaciones',
          'pt-BR': 'Meus reparos',
          ar: 'إصلاحاتي',
          'ar-SY': 'تصليحاتي',
          zh: '我的维修',
        },
        where: {
          fr: 'Onglet « Réparations ».',
          en: 'The “Repairs” tab.',
          es: 'Pestaña «Reparaciones».',
          'pt-BR': 'Aba «Reparos».',
          ar: 'علامة التبويب «الإصلاحات».',
          'ar-SY': 'تبويب «التصليحات».',
          zh: '“维修”选项卡。',
        },
        steps: {
          fr: [
            '« Demander une réparation » : marque, modèle, motif, vos coordonnées et des photos. Un diagnostic de 35 € est demandé.',
            'Payez le diagnostic en ligne (SumUp) ou en boutique ; suivez le statut et la barre de progression.',
            '« Suivi détaillé » montre la chronologie ; « Notes du technicien » ses photos/vidéos ; « Contacter » ouvre le chat.',
            'Si vous envoyez l’appareil par la poste, indiquez le numéro de suivi ; le délai est alors prolongé.',
          ],
          en: [
            '“Request a repair”: brand, model, issue, your details and photos. A €35 diagnostic is required.',
            'Pay the diagnostic online (SumUp) or in store; follow the status and progress bar.',
            '“Detailed tracking” shows the timeline; “Technician notes” their photos/videos; “Contact” opens the chat.',
            'If you post the device, enter the tracking number; the deadline is then extended.',
          ],
          es: [
            '«Solicitar una reparación»: marca, modelo, motivo, sus datos de contacto y fotos. Se solicita un diagnóstico de 35 €.',
            'Pague el diagnóstico en línea (SumUp) o en la tienda; siga el estado y la barra de progreso.',
            '«Seguimiento detallado» muestra la cronología; «Notas del técnico» sus fotos/vídeos; «Contactar» abre el chat.',
            'Si envía el dispositivo por correo, indique el número de seguimiento; el plazo se amplía entonces.',
          ],
          'pt-BR': [
            '«Solicitar um reparo»: marca, modelo, motivo, seus dados de contato e fotos. É solicitado um diagnóstico de 35 €.',
            'Pague o diagnóstico on-line (SumUp) ou na loja; acompanhe o status e a barra de progresso.',
            '«Acompanhamento detalhado» mostra a cronologia; «Notas do técnico» as fotos/vídeos dele; «Contatar» abre o chat.',
            'Se você enviar o aparelho pelos Correios, informe o número de rastreamento; o prazo é então prorrogado.',
          ],
          ar: [
            '«طلب إصلاح»: العلامة التجارية، الطراز، السبب، بيانات الاتصال الخاصة بك وصور. يُطلَب تشخيص بقيمة 35 €.',
            'ادفع رسوم التشخيص عبر الإنترنت (SumUp) أو في المتجر؛ تابِع الحالة وشريط التقدّم.',
            '«التتبّع المفصّل» يعرض التسلسل الزمني؛ «ملاحظات الفنّي» صوره/فيديوهاته؛ «التواصل» يفتح المحادثة.',
            'إذا أرسلت الجهاز بالبريد، أدخِل رقم التتبّع؛ عندها يُمدَّد الموعد النهائي.',
          ],
          'ar-SY': [
            '«اطلب تصليح»: الماركة، الموديل، السبب، معلومات التواصل تبعك وصور. بينطلب فحص بـ 35 €.',
            'إدفع رسوم الفحص أونلاين (SumUp) أو بالمحل؛ تابِع الحالة وشريط التقدّم.',
            '«التتبّع المفصّل» بيبيّن التسلسل الزمني؛ «ملاحظات الفنّي» صورو/فيديوهاتو؛ «تواصل» بيفتح المحادثة.',
            'إذا بعتّ الجهاز بالبريد، دخّل رقم التتبّع؛ ساعتها بينمدّ الموعد.',
          ],
          zh: [
            '“申请维修”：品牌、型号、故障原因、您的联系方式和照片。需支付 35 欧元检测费。',
            '在线（SumUp）或在门店支付检测费；跟踪状态和进度条。',
            '“详细追踪”显示时间线；“技师备注”显示其照片/视频；“联系”打开聊天。',
            '如果您邮寄设备，请填写快递单号；此时期限会相应延长。',
          ],
        },
        can: {
          fr: ['Demander, payer, suivre et discuter d’une réparation en ligne.'],
          en: ['Request, pay, track and chat about a repair online.'],
          es: ['Solicitar, pagar, seguir y hablar sobre una reparación en línea.'],
          'pt-BR': ['Solicitar, pagar, acompanhar e conversar sobre um reparo on-line.'],
          ar: ['طلب إصلاح ودفعه وتتبّعه والتحدّث بشأنه عبر الإنترنت.'],
          'ar-SY': ['تطلب وتدفع وتتبّع وتحكي عن تصليح أونلاين.'],
          zh: ['在线申请、支付、追踪维修并就维修沟通。'],
        },
        cannot: {
          fr: ['Annuler une réparation en ligne (contactez la boutique).'],
          en: ['Cancel a repair online (contact the shop).'],
          es: ['Cancelar una reparación en línea (contacte con la tienda).'],
          'pt-BR': ['Cancelar um reparo on-line (entre em contato com a loja).'],
          ar: ['إلغاء إصلاح عبر الإنترنت (تواصل مع المتجر).'],
          'ar-SY': ['تلغي تصليح أونلاين (تواصل مع المحل).'],
          zh: ['在线取消维修（请联系门店）。'],
        },
      },
      {
        id: 'boutique',
        icon: 'ShoppingBag',
        title: {
          fr: 'La boutique',
          en: 'The shop',
          es: 'La tienda',
          'pt-BR': 'A loja',
          ar: 'المتجر',
          'ar-SY': 'المتجر',
          zh: '商店',
        },
        where: {
          fr: 'Onglet « Boutique ».',
          en: 'The “Shop” tab.',
          es: 'Pestaña «Tienda».',
          'pt-BR': 'Aba «Loja».',
          ar: 'علامة التبويب «المتجر».',
          'ar-SY': 'تبويب «المتجر».',
          zh: '“商店”选项卡。',
        },
        steps: {
          fr: [
            'Parcourez les articles (filtre par catégorie), ajoutez au panier les articles en stock.',
            'Dans le panier, choisissez une adresse de livraison, puis payez avec SumUp ou choisissez « Paiement à la livraison ».',
          ],
          en: [
            'Browse items (filter by category), add in-stock items to the cart.',
            'In the cart, choose a delivery address, then pay with SumUp or pick “Pay on delivery”.',
          ],
          es: [
            'Explore los artículos (filtro por categoría) y añada al carrito los artículos en stock.',
            'En el carrito, elija una dirección de entrega y luego pague con SumUp o elija «Pago contra entrega».',
          ],
          'pt-BR': [
            'Navegue pelos itens (filtro por categoria) e adicione ao carrinho os itens em estoque.',
            'No carrinho, escolha um endereço de entrega e pague com SumUp ou escolha «Pagamento na entrega».',
          ],
          ar: [
            'تصفّح المنتجات (تصفية حسب الفئة)، وأضِف إلى السلّة المنتجات المتوفّرة في المخزون.',
            'في السلّة، اختر عنوان التوصيل، ثم ادفع عبر SumUp أو اختر «الدفع عند التسليم».',
          ],
          'ar-SY': [
            'تصفّح المنتجات (فلتر حسب الفئة)، وضيف عالسلّة المنتجات المتوفّرة.',
            'بالسلّة، اختار عنوان التوصيل، وبعدين ادفع بـ SumUp أو اختار «الدفع عند الاستلام».',
          ],
          zh: [
            '浏览商品（按类别筛选），将有货商品加入购物车。',
            '在购物车中选择送货地址，然后用 SumUp 付款或选择“货到付款”。',
          ],
        },
        can: {
          fr: ['Commander des accessoires en ligne, payés d’avance ou à la livraison.'],
          en: ['Order accessories online, prepaid or on delivery.'],
          es: ['Pedir accesorios en línea, pagados por adelantado o contra entrega.'],
          'pt-BR': ['Pedir acessórios on-line, pagos antecipadamente ou na entrega.'],
          ar: ['طلب الإكسسوارات عبر الإنترنت، بالدفع المسبق أو عند التسليم.'],
          'ar-SY': ['تطلب إكسسوارات أونلاين، مدفوعة مسبقًا أو عند الاستلام.'],
          zh: ['在线订购配件，可预付或货到付款。'],
        },
        cannot: {
          fr: ['Commander un article en rupture, ni régler sans avoir choisi une adresse.'],
          en: ['Order an out-of-stock item, or pay without selecting an address.'],
          es: ['Pedir un artículo agotado ni pagar sin haber elegido una dirección.'],
          'pt-BR': ['Pedir um item esgotado ou pagar sem ter escolhido um endereço.'],
          ar: ['طلب منتج غير متوفّر في المخزون، أو الدفع دون اختيار عنوان.'],
          'ar-SY': ['تطلب منتج مو متوفّر، أو تدفع بدون ما تختار عنوان.'],
          zh: ['订购缺货商品，或未选择地址就付款。'],
        },
      },
      {
        id: 'commandes',
        icon: 'Package',
        title: {
          fr: 'Mes commandes',
          en: 'My orders',
          es: 'Mis pedidos',
          'pt-BR': 'Meus pedidos',
          ar: 'طلباتي',
          'ar-SY': 'طلباتي',
          zh: '我的订单',
        },
        where: {
          fr: 'Onglet « Commandes ».',
          en: 'The “Orders” tab.',
          es: 'Pestaña «Pedidos».',
          'pt-BR': 'Aba «Pedidos».',
          ar: 'علامة التبويب «الطلبات».',
          'ar-SY': 'تبويب «الطلبات».',
          zh: '“订单”选项卡。',
        },
        steps: {
          fr: [
            'Suivez chaque commande : statut, numéro de suivi, étapes de préparation à la livraison.',
            'Si elle est en attente, payez avec SumUp, ou « Envoyer une preuve de paiement » (capture) que le staff valide.',
            '« Contacter » ouvre un chat lié à la commande.',
          ],
          en: [
            'Track each order: status, tracking number, steps from preparation to delivery.',
            'If pending, pay with SumUp, or “Send a payment proof” (screenshot) that staff validate.',
            '“Contact” opens a chat tied to the order.',
          ],
          es: [
            'Siga cada pedido: estado, número de seguimiento, etapas desde la preparación hasta la entrega.',
            'Si está pendiente, pague con SumUp o «Enviar un comprobante de pago» (captura) que el staff valida.',
            '«Contactar» abre un chat vinculado al pedido.',
          ],
          'pt-BR': [
            'Acompanhe cada pedido: status, número de rastreamento, etapas da preparação até a entrega.',
            'Se estiver pendente, pague com SumUp ou «Enviar um comprovante de pagamento» (captura) que o staff valida.',
            '«Contatar» abre um chat vinculado ao pedido.',
          ],
          ar: [
            'تابِع كل طلب: الحالة، رقم التتبّع، المراحل من التحضير إلى التسليم.',
            'إذا كان قيد الانتظار، ادفع عبر SumUp، أو «إرسال إثبات الدفع» (لقطة شاشة) يصادق عليه الفريق.',
            '«التواصل» يفتح محادثة مرتبطة بالطلب.',
          ],
          'ar-SY': [
            'تابِع كل طلب: الحالة، رقم التتبّع، المراحل من التحضير لحتى التسليم.',
            'إذا كان قيد الانتظار، ادفع بـ SumUp، أو «إرسال إثبات الدفع» (صورة شاشة) وبيأكّدو الفريق.',
            '«تواصل» بيفتح محادثة مربوطة بالطلب.',
          ],
          zh: [
            '跟踪每笔订单：状态、快递单号、从备货到送达的各个环节。',
            '如待付款，用 SumUp 付款，或“发送付款凭证”（截图）供员工核实。',
            '“联系”会打开与该订单关联的聊天。',
          ],
        },
        can: {
          fr: ['Payer, envoyer une preuve de paiement et suivre la livraison.'],
          en: ['Pay, send a payment proof and follow delivery.'],
          es: ['Pagar, enviar un comprobante de pago y seguir la entrega.'],
          'pt-BR': ['Pagar, enviar um comprovante de pagamento e acompanhar a entrega.'],
          ar: ['الدفع وإرسال إثبات الدفع وتتبّع التسليم.'],
          'ar-SY': ['تدفع وتبعت إثبات دفع وتتبّع التسليم.'],
          zh: ['付款、发送付款凭证并跟踪送货。'],
        },
        cannot: {
          fr: ['Annuler ou modifier une commande : son statut est géré par le magasin.'],
          en: ['Cancel or change an order: its status is managed by the shop.'],
          es: ['Cancelar o modificar un pedido: su estado lo gestiona la tienda.'],
          'pt-BR': ['Cancelar ou modificar um pedido: seu status é gerenciado pela loja.'],
          ar: ['إلغاء أو تعديل طلب: حالته يديرها المتجر.'],
          'ar-SY': ['تلغي أو تعدّل طلب: حالتو بيديرها المحل.'],
          zh: ['取消或修改订单：其状态由门店管理。'],
        },
      },
      {
        id: 'devis-factures',
        icon: 'ReceiptText',
        title: {
          fr: 'Devis & factures',
          en: 'Quotes & invoices',
          es: 'Presupuestos y facturas',
          'pt-BR': 'Orçamentos e faturas',
          ar: 'عروض الأسعار والفواتير',
          'ar-SY': 'عروض الأسعار والفواتير',
          zh: '报价与发票',
        },
        where: {
          fr: 'Onglet « Devis / Factures ».',
          en: 'The “Quotes / Invoices” tab.',
          es: 'Pestaña «Presupuestos / Facturas».',
          'pt-BR': 'Aba «Orçamentos / Faturas».',
          ar: 'علامة التبويب «عروض الأسعار / الفواتير».',
          'ar-SY': 'تبويب «عروض الأسعار / الفواتير».',
          zh: '“报价 / 发票”选项卡。',
        },
        steps: {
          fr: [
            'Fidélité : votre niveau (Bronze/Argent/Or), votre remise, vos points et la récompense débloquée à 10 achats.',
            'Parrainage : votre code et un lien à partager ; +2 points par filleul inscrit.',
            '« Nouvelle demande » de devis : décrivez le besoin (et l’adresse pour un déplacement).',
            'Un devis reçu : « Accepter / Refuser ». Après acceptation, entrez le code à 6 chiffres pour débloquer le bon de commande et le paiement.',
            'Payez un acompte de 50 % ou la totalité ; téléchargez les PDF (devis, bon de commande, facture).',
            'Vos factures de caisse sont consultables, imprimables et téléchargeables.',
          ],
          en: [
            'Loyalty: your tier (Bronze/Silver/Gold), your discount, your points and the reward unlocked at 10 purchases.',
            'Referral: your code and a share link; +2 points per registered referral.',
            '“New request” for a quote: describe the need (and the address for an on-site visit).',
            'A received quote: “Accept / Decline”. After accepting, enter the 6-digit code to unlock the order form and payment.',
            'Pay a 50% deposit or the full amount; download the PDFs (quote, order form, invoice).',
            'Your point-of-sale invoices can be viewed, printed and downloaded.',
          ],
          es: [
            'Fidelidad: su nivel (Bronce/Plata/Oro), su descuento, sus puntos y la recompensa desbloqueada a las 10 compras.',
            'Patrocinio: su código y un enlace para compartir; +2 puntos por cada referido registrado.',
            '«Nueva solicitud» de presupuesto: describa la necesidad (y la dirección para un desplazamiento).',
            'Un presupuesto recibido: «Aceptar / Rechazar». Tras aceptar, introduzca el código de 6 dígitos para desbloquear la orden de pedido y el pago.',
            'Pague un anticipo del 50 % o la totalidad; descargue los PDF (presupuesto, orden de pedido, factura).',
            'Sus tickets de caja se pueden consultar, imprimir y descargar.',
          ],
          'pt-BR': [
            'Fidelidade: seu nível (Bronze/Prata/Ouro), seu desconto, seus pontos e a recompensa desbloqueada às 10 compras.',
            'Indicação: seu código e um link para compartilhar; +2 pontos por indicado cadastrado.',
            '«Nova solicitação» de orçamento: descreva a necessidade (e o endereço para uma visita).',
            'Um orçamento recebido: «Aceitar / Recusar». Após aceitar, digite o código de 6 dígitos para desbloquear o pedido de compra e o pagamento.',
            'Pague um sinal de 50 % ou o valor total; baixe os PDFs (orçamento, pedido de compra, fatura).',
            'Seus cupons fiscais podem ser consultados, impressos e baixados.',
          ],
          ar: [
            'الولاء: مستواك (برونزي/فضّي/ذهبي)، الخصم الخاص بك، نقاطك والمكافأة التي تُفتَح عند 10 عمليات شراء.',
            'الإحالة: رمزك ورابط للمشاركة؛ +2 نقطة لكل مُحال مسجَّل.',
            '«طلب جديد» لعرض سعر: صِف حاجتك (والعنوان في حال التنقّل إليك).',
            'عند استلام عرض سعر: «قبول / رفض». بعد القبول، أدخِل الرمز المكوَّن من 6 أرقام لفتح أمر الشراء والدفع.',
            'ادفع عربونًا بنسبة 50 % أو المبلغ كاملًا؛ نزّل ملفات PDF (عرض السعر، أمر الشراء، الفاتورة).',
            'يمكن عرض فواتير نقطة البيع وطباعتها وتنزيلها.',
          ],
          'ar-SY': [
            'الولاء: مستواك (برونزي/فضّي/ذهبي)، الخصم تبعك، نقاطك والمكافأة يلي بتنفتح عند 10 مشتريات.',
            'الإحالة: رمزك ورابط تشاركو؛ +2 نقطة لكل شخص محوّل ومسجّل.',
            '«طلب جديد» لعرض سعر: وصّف شو بدّك (والعنوان إذا بدّن يجوا عندك).',
            'لمّا يوصلك عرض سعر: «قبول / رفض». بعد ما تقبل، دخّل الرمز المكوّن من 6 أرقام لتفتح أمر الشرا والدفع.',
            'ادفع عربون 50 % أو المبلغ كلّو؛ نزّل ملفات PDF (عرض السعر، أمر الشرا، الفاتورة).',
            'فواتير الكاش فيك تشوفها وتطبعها وتنزّلها.',
          ],
          zh: [
            '会员：您的等级（青铜/白银/黄金）、您的折扣、您的积分，以及满 10 次购买解锁的奖励。',
            '推荐：您的推荐码和可分享的链接；每位成功注册的被推荐人 +2 分。',
            '报价“新申请”：描述您的需求（如需上门，还需地址）。',
            '收到报价后：“接受 / 拒绝”。接受后，输入 6 位验证码以解锁订购单和付款。',
            '支付 50 % 定金或全款；下载 PDF（报价单、订购单、发票）。',
            '您的收银发票可查看、打印和下载。',
          ],
        },
        can: {
          fr: ['Demander, accepter et payer un devis ; suivre fidélité et parrainage ; récupérer vos PDF.'],
          en: ['Request, accept and pay a quote; track loyalty and referrals; get your PDFs.'],
          es: ['Solicitar, aceptar y pagar un presupuesto; seguir fidelidad y patrocinio; obtener sus PDF.'],
          'pt-BR': ['Solicitar, aceitar e pagar um orçamento; acompanhar fidelidade e indicações; obter seus PDFs.'],
          ar: ['طلب عرض سعر وقبوله ودفعه؛ تتبّع الولاء والإحالة؛ الحصول على ملفات PDF الخاصة بك.'],
          'ar-SY': ['تطلب وتقبل وتدفع عرض سعر؛ تتبّع الولاء والإحالة؛ تجيب ملفات PDF تبعك.'],
          zh: ['申请、接受并支付报价；追踪会员与推荐；获取您的 PDF。'],
        },
        cannot: {
          fr: ['Payer ou obtenir le bon de commande avant d’avoir accepté et saisi le code de validation à 6 chiffres.'],
          en: ['Pay or get the order form before accepting and entering the 6-digit validation code.'],
          es: ['Pagar u obtener la orden de pedido antes de haber aceptado e introducido el código de validación de 6 dígitos.'],
          'pt-BR': ['Pagar ou obter o pedido de compra antes de aceitar e digitar o código de validação de 6 dígitos.'],
          ar: ['الدفع أو الحصول على أمر الشراء قبل القبول وإدخال رمز التحقّق المكوَّن من 6 أرقام.'],
          'ar-SY': ['تدفع أو تجيب أمر الشرا قبل ما تقبل وتدخّل رمز التحقّق المكوّن من 6 أرقام.'],
          zh: ['在接受并输入 6 位验证码之前付款或获取订购单。'],
        },
      },
      {
        id: 'adresses',
        icon: 'MapPinned',
        title: {
          fr: 'Mes adresses',
          en: 'My addresses',
          es: 'Mis direcciones',
          'pt-BR': 'Meus endereços',
          ar: 'عناويني',
          'ar-SY': 'عناويني',
          zh: '我的地址',
        },
        where: {
          fr: 'Onglet « Mes adresses ».',
          en: 'The “My addresses” tab.',
          es: 'Pestaña «Mis direcciones».',
          'pt-BR': 'Aba «Meus endereços».',
          ar: 'علامة التبويب «عناويني».',
          'ar-SY': 'تبويب «عناويني».',
          zh: '“我的地址”选项卡。',
        },
        steps: {
          fr: ['Ajoutez, modifiez ou supprimez vos adresses de livraison, et définissez celle par défaut.'],
          en: ['Add, edit or delete your delivery addresses, and set a default one.'],
          es: ['Añada, modifique o elimine sus direcciones de entrega, y defina la predeterminada.'],
          'pt-BR': ['Adicione, edite ou exclua seus endereços de entrega e defina o padrão.'],
          ar: ['أضِف عناوين التوصيل الخاصة بك أو عدّلها أو احذفها، وحدّد العنوان الافتراضي.'],
          'ar-SY': ['ضيف أو عدّل أو احذف عناوين التوصيل تبعك، وحدّد العنوان الافتراضي.'],
          zh: ['添加、修改或删除您的送货地址，并设置默认地址。'],
        },
        can: {
          fr: ['Gérer plusieurs adresses de livraison.'],
          en: ['Manage several delivery addresses.'],
          es: ['Gestionar varias direcciones de entrega.'],
          'pt-BR': ['Gerenciar vários endereços de entrega.'],
          ar: ['إدارة عدّة عناوين توصيل.'],
          'ar-SY': ['تدير كذا عنوان توصيل.'],
          zh: ['管理多个送货地址。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'chat',
        icon: 'MessageCircle',
        title: {
          fr: 'Chat & aide',
          en: 'Chat & help',
          es: 'Chat y ayuda',
          'pt-BR': 'Chat e ajuda',
          ar: 'المحادثة والمساعدة',
          'ar-SY': 'المحادثة والمساعدة',
          zh: '聊天与帮助',
        },
        where: {
          fr: 'Bulle de chat flottante.',
          en: 'Floating chat bubble.',
          es: 'Burbuja de chat flotante.',
          'pt-BR': 'Balão de chat flutuante.',
          ar: 'فقاعة محادثة عائمة.',
          'ar-SY': 'فقاعة محادثة عايمة.',
          zh: '浮动聊天气泡。',
        },
        steps: {
          fr: [
            'Onglet « Staff » : discutez avec l’équipe. Hors horaires, un assistant IA répond automatiquement.',
            'Onglet « Aide » : questions fréquentes, mini-visites guidées et une IA qui répond à vos questions sur le site.',
          ],
          en: [
            '“Staff” tab: chat with the team. Outside hours, an AI assistant replies automatically.',
            '“Help” tab: FAQ, mini guided tours and an AI that answers your questions about the site.',
          ],
          es: [
            'Pestaña «Staff»: converse con el equipo. Fuera del horario, un asistente de IA responde automáticamente.',
            'Pestaña «Ayuda»: preguntas frecuentes, mini visitas guiadas y una IA que responde a sus preguntas sobre el sitio.',
          ],
          'pt-BR': [
            'Aba «Staff»: converse com a equipe. Fora do horário, um assistente de IA responde automaticamente.',
            'Aba «Ajuda»: perguntas frequentes, mini visitas guiadas e uma IA que responde às suas perguntas sobre o site.',
          ],
          ar: [
            'علامة التبويب «الفريق»: تحدّث مع الفريق. خارج أوقات العمل، يردّ مساعد ذكاء اصطناعي تلقائيًا.',
            'علامة التبويب «المساعدة»: الأسئلة الشائعة، جولات إرشادية مصغّرة وذكاء اصطناعي يجيب عن أسئلتك حول الموقع.',
          ],
          'ar-SY': [
            'تبويب «الفريق»: احكي مع الفريق. خارج أوقات الدوام، في مساعد ذكاء اصطناعي بيردّ أوتوماتيكي.',
            'تبويب «المساعدة»: الأسئلة الشائعة، جولات إرشادية صغيرة وذكاء اصطناعي بيجاوب ع أسئلتك حول الموقع.',
          ],
          zh: [
            '“员工”选项卡：与团队聊天。非营业时间，AI 助手会自动回复。',
            '“帮助”选项卡：常见问题、迷你导览，以及解答您网站相关问题的 AI。',
          ],
        },
        can: {
          fr: ['Joindre l’équipe et obtenir de l’aide à tout moment.'],
          en: ['Reach the team and get help anytime.'],
          es: ['Contactar con el equipo y obtener ayuda en cualquier momento.'],
          'pt-BR': ['Falar com a equipe e obter ajuda a qualquer momento.'],
          ar: ['التواصل مع الفريق والحصول على المساعدة في أي وقت.'],
          'ar-SY': ['تتواصل مع الفريق وتاخد مساعدة بأي وقت.'],
          zh: ['随时联系团队并获得帮助。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'notifications',
        icon: 'Bell',
        title: {
          fr: 'Notifications',
          en: 'Notifications',
          es: 'Notificaciones',
          'pt-BR': 'Notificações',
          ar: 'الإشعارات',
          'ar-SY': 'الإشعارات',
          zh: '通知',
        },
        where: {
          fr: 'Cloche en haut de l’espace client.',
          en: 'Bell at the top of the client area.',
          es: 'Campana en la parte superior del espacio de cliente.',
          'pt-BR': 'Sino no topo da área do cliente.',
          ar: 'الجرس في أعلى مساحة العميل.',
          'ar-SY': 'الجرس بأعلى مساحة الزبون.',
          zh: '客户专区顶部的铃铛。',
        },
        steps: {
          fr: ['Un pastille indique les nouveautés (changement de statut, message). Cliquez pour lire, « Tout marquer lu » pour vider.'],
          en: ['A badge shows what’s new (status change, message). Click to read, “Mark all read” to clear.'],
          es: ['Un indicador muestra las novedades (cambio de estado, mensaje). Haga clic para leer, «Marcar todo como leído» para vaciar.'],
          'pt-BR': ['Um selo mostra as novidades (mudança de status, mensagem). Clique para ler, «Marcar tudo como lido» para limpar.'],
          ar: ['تُظهِر نقطة صغيرة الجديد (تغيير الحالة، رسالة). انقر للقراءة، و«تحديد الكل كمقروء» للتفريغ.'],
          'ar-SY': ['في نقطة صغيرة بتبيّن الجديد (تغيير الحالة، رسالة). دوس لتقرا، و«علّم الكل كمقروء» لتفضّي.'],
          zh: ['小红点提示新动态（状态变更、消息）。点击阅读，“全部标为已读”可清空。'],
        },
        can: {
          fr: ['Être averti à chaque changement de statut ou message.'],
          en: ['Be alerted on every status change or message.'],
          es: ['Recibir un aviso en cada cambio de estado o mensaje.'],
          'pt-BR': ['Ser avisado a cada mudança de status ou mensagem.'],
          ar: ['تلقّي تنبيه عند كل تغيير في الحالة أو رسالة.'],
          'ar-SY': ['يوصلك تنبيه عند كل تغيير بالحالة أو رسالة.'],
          zh: ['每次状态变更或收到消息时都会收到提醒。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
      {
        id: 'caricature',
        icon: 'Smile',
        title: {
          fr: 'Ma caricature',
          en: 'My caricature',
          es: 'Mi caricatura',
          'pt-BR': 'Minha caricatura',
          ar: 'رسمي الكاريكاتيري',
          'ar-SY': 'رسمتي الكاريكاتيرية',
          zh: '我的漫画像',
        },
        where: {
          fr: 'Lien « Je crée ma caricature » (clients R.E.Mobiles).',
          en: 'The “Create my caricature” link (R.E.Mobiles clients).',
          es: 'Enlace «Creo mi caricatura» (clientes R.E.Mobiles).',
          'pt-BR': 'Link «Criar minha caricatura» (clientes R.E.Mobiles).',
          ar: 'رابط «أنشئ رسمي الكاريكاتيري» (عملاء R.E.Mobiles).',
          'ar-SY': 'رابط «إعمل رسمتي الكاريكاتيرية» (زباين R.E.Mobiles).',
          zh: '“创建我的漫画像”链接（R.E.Mobiles 客户）。',
        },
        steps: {
          fr: ['Un lien vers un service partenaire pour créer votre caricature.'],
          en: ['A link to a partner service to create your caricature.'],
          es: ['Un enlace a un servicio asociado para crear su caricatura.'],
          'pt-BR': ['Um link para um serviço parceiro para criar sua caricatura.'],
          ar: ['رابط إلى خدمة شريكة لإنشاء رسمك الكاريكاتيري.'],
          'ar-SY': ['رابط لخدمة شريكة لتعمل رسمتك الكاريكاتيرية.'],
          zh: ['指向合作伙伴服务的链接，用于创建您的漫画像。'],
        },
        can: {
          fr: ['Accéder au service de caricature.'],
          en: ['Access the caricature service.'],
          es: ['Acceder al servicio de caricatura.'],
          'pt-BR': ['Acessar o serviço de caricatura.'],
          ar: ['الوصول إلى خدمة الرسم الكاريكاتيري.'],
          'ar-SY': ['تفوت ع خدمة الرسم الكاريكاتيري.'],
          zh: ['使用漫画像服务。'],
        },
        cannot: { fr: [], en: [], es: [], 'pt-BR': [], ar: [], 'ar-SY': [], zh: [] },
      },
    ],
  },
];

/** Récapitulatif « ce que le client ne peut pas faire » (repris en fin de guide). */
export const AIDE_LIMITS = {
  fr: [
    'Voir les prix d’achat ou les marges : jamais affichés.',
    'Annuler ou modifier une vente, une commande ou une réparation : géré par le magasin.',
    'Entrer dans les espaces Staff ou Entreprise avec un compte client.',
    'Obtenir un devis sans donner son nom, son téléphone et son e-mail.',
    'Payer un devis avant de l’avoir accepté et d’avoir saisi le code de validation.',
  ],
  en: [
    'See purchase prices or margins: never shown.',
    'Cancel or change a sale, order or repair: managed by the shop.',
    'Enter the Staff or Business areas with a client account.',
    'Get a quote without giving name, phone and e-mail.',
    'Pay a quote before accepting it and entering the validation code.',
  ],
  es: [
    'Ver los precios de compra o los márgenes: nunca se muestran.',
    'Cancelar o modificar una venta, un pedido o una reparación: lo gestiona la tienda.',
    'Entrar en los espacios Staff o Empresa con una cuenta de cliente.',
    'Obtener un presupuesto sin dar su nombre, su teléfono y su correo electrónico.',
    'Pagar un presupuesto antes de haberlo aceptado e introducido el código de validación.',
  ],
  'pt-BR': [
    'Ver os preços de compra ou as margens: nunca são exibidos.',
    'Cancelar ou modificar uma venda, um pedido ou um reparo: gerenciado pela loja.',
    'Entrar nas áreas Staff ou Empresa com uma conta de cliente.',
    'Obter um orçamento sem informar nome, telefone e e-mail.',
    'Pagar um orçamento antes de tê-lo aceitado e digitado o código de validação.',
  ],
  ar: [
    'رؤية أسعار الشراء أو هوامش الربح: لا تُعرَض أبدًا.',
    'إلغاء أو تعديل عملية بيع أو طلب أو إصلاح: يديرها المتجر.',
    'الدخول إلى مساحتَي الفريق أو الشركات بحساب عميل.',
    'الحصول على عرض سعر دون تقديم الاسم والهاتف والبريد الإلكتروني.',
    'دفع عرض سعر قبل قبوله وإدخال رمز التحقّق.',
  ],
  'ar-SY': [
    'تشوف أسعار الشرا أو الأرباح: ما بتنعرض أبدًا.',
    'تلغي أو تعدّل عملية بيع أو طلب أو تصليح: بيديرها المحل.',
    'تفوت ع مساحات الفريق أو الشركات بحساب زبون.',
    'تاخد عرض سعر بدون ما تعطي اسمك وتلفونك وإيميلك.',
    'تدفع عرض سعر قبل ما تقبلو وتدخّل رمز التحقّق.',
  ],
  zh: [
    '查看进货价或利润：从不显示。',
    '取消或修改销售、订单或维修：由门店管理。',
    '用客户账户进入员工或企业专区。',
    '不提供姓名、电话和电子邮箱就获取报价。',
    '在接受报价并输入验证码之前就付款。',
  ],
};

export const AIDE_UI = {
  title: {
    fr: 'Guide-moi',
    en: 'Guide me',
    es: 'Guíame',
    'pt-BR': 'Me guie',
    ar: 'أرشِدني',
    'ar-SY': 'دلّني',
    zh: '指引我',
  },
  subtitle: {
    fr: 'Comment utiliser le site',
    en: 'How to use the site',
    es: 'Cómo usar el sitio',
    'pt-BR': 'Como usar o site',
    ar: 'كيفية استخدام الموقع',
    'ar-SY': 'كيف تستخدم الموقع',
    zh: '如何使用本网站',
  },
  intro: {
    fr: 'Ce guide vous montre, pas à pas, tout ce que vous pouvez faire sur le site — sur la vitrine comme dans votre espace client.',
    en: 'This guide shows you, step by step, everything you can do on the site — on the public site and in your client area.',
    es: 'Esta guía le muestra, paso a paso, todo lo que puede hacer en el sitio — tanto en el sitio público como en su espacio de cliente.',
    'pt-BR': 'Este guia mostra, passo a passo, tudo o que você pode fazer no site — tanto no site público quanto na sua área do cliente.',
    ar: 'يوضّح لك هذا الدليل، خطوة بخطوة، كل ما يمكنك فعله على الموقع — في الواجهة العامة وفي مساحة العميل الخاصة بك.',
    'ar-SY': 'هالدليل بيوريك، خطوة بخطوة، كل شي فيك تعملو عالموقع — بالواجهة العامة ومساحة الزبون تبعتك.',
    zh: '本指南逐步向您展示在本网站上可以做的一切——包括公开网站和您的客户专区。',
  },
  cinema: {
    fr: 'Lecture guidée',
    en: 'Guided play',
    es: 'Reproducción guiada',
    'pt-BR': 'Reprodução guiada',
    ar: 'تشغيل موجَّه',
    'ar-SY': 'تشغيل موجَّه',
    zh: '引导播放',
  },
  search: {
    fr: 'Rechercher dans le guide…',
    en: 'Search the guide…',
    es: 'Buscar en la guía…',
    'pt-BR': 'Pesquisar no guia…',
    ar: 'البحث في الدليل…',
    'ar-SY': 'دوّر بالدليل…',
    zh: '在指南中搜索……',
  },
  canLabel: {
    fr: 'Vous pouvez',
    en: 'You can',
    es: 'Puede',
    'pt-BR': 'Você pode',
    ar: 'يمكنك',
    'ar-SY': 'فيك',
    zh: '您可以',
  },
  cannotLabel: {
    fr: 'À savoir',
    en: 'Good to know',
    es: 'Para tener en cuenta',
    'pt-BR': 'Bom saber',
    ar: 'معلومة مفيدة',
    'ar-SY': 'معلومة مفيدة',
    zh: '须知',
  },
  whereLabel: {
    fr: 'Où',
    en: 'Where',
    es: 'Dónde',
    'pt-BR': 'Onde',
    ar: 'أين',
    'ar-SY': 'وين',
    zh: '位置',
  },
  limitsTitle: {
    fr: 'Ce que vous ne pouvez pas faire',
    en: 'What you cannot do',
    es: 'Lo que no puede hacer',
    'pt-BR': 'O que você não pode fazer',
    ar: 'ما لا يمكنك فعله',
    'ar-SY': 'شو ما فيك تعمل',
    zh: '您无法做的事',
  },

  // ── Chrome de la page /aide (ex-ternaires FR/EN, désormais 7 langues) ──
  homeAria: {
    fr: "Retour à l'accueil",
    en: 'Back to home',
    es: 'Volver al inicio',
    'pt-BR': 'Voltar ao início',
    ar: 'العودة إلى الصفحة الرئيسية',
    'ar-SY': 'ارجع عالصفحة الرئيسية',
    zh: '返回首页',
  },
  home: {
    fr: 'Accueil',
    en: 'Home',
    es: 'Inicio',
    'pt-BR': 'Início',
    ar: 'الرئيسية',
    'ar-SY': 'الرئيسية',
    zh: '首页',
  },
  langGroupAria: {
    fr: 'Langue du guide',
    en: 'Guide language',
    es: 'Idioma de la guía',
    'pt-BR': 'Idioma do guia',
    ar: 'لغة الدليل',
    'ar-SY': 'لغة الدليل',
    zh: '指南语言',
  },
  videosAria: {
    fr: 'Vidéos de démonstration',
    en: 'Demonstration videos',
    es: 'Vídeos de demostración',
    'pt-BR': 'Vídeos de demonstração',
    ar: 'فيديوهات توضيحية',
    'ar-SY': 'فيديوهات توضيحية',
    zh: '演示视频',
  },
  videoTitle: {
    fr: 'Vidéo de démonstration',
    en: 'Video demo',
    es: 'Vídeo de demostración',
    'pt-BR': 'Vídeo de demonstração',
    ar: 'فيديو توضيحي',
    'ar-SY': 'فيديو توضيحي',
    zh: '演示视频',
  },
  videoFinalising: {
    fr: 'La vidéo est en cours de finalisation.',
    en: 'The video is being finalised.',
    es: 'El vídeo se está finalizando.',
    'pt-BR': 'O vídeo está sendo finalizado.',
    ar: 'يجري حاليًا إنهاء الفيديو.',
    'ar-SY': 'الفيديو لسا عم يتجهّز.',
    zh: '视频正在制作中。',
  },
  videoCaption: {
    fr: 'Capture en temps réel du vrai site, avec des données fictives. Le numéro de téléphone du compte de démonstration est masqué.',
    en: 'Real-time capture of the actual site, with fictitious data. The phone number of the demo account is masked.',
    es: 'Captura en tiempo real del sitio real, con datos ficticios. El número de teléfono de la cuenta de demostración está oculto.',
    'pt-BR': 'Captura em tempo real do site real, com dados fictícios. O número de telefone da conta de demonstração está oculto.',
    ar: 'تسجيل مباشر للموقع الحقيقي ببيانات وهمية. رقم هاتف حساب العرض التوضيحي مُخفى.',
    'ar-SY': 'تسجيل مباشر للموقع الحقيقي ببيانات وهمية. رقم تلفون حساب العرض التوضيحي مخفي.',
    zh: '实时录制真实网站，使用虚构数据。演示账户的电话号码已隐去。',
  },
  // Note affichée quand la vidéo localisée n'existe pas encore et qu'on retombe sur EN/FR.
  videoFallbackNote: {
    fr: 'La vidéo dans cette langue arrive bientôt — version anglaise affichée.',
    en: 'The video in this language is coming soon — English version shown.',
    es: 'El vídeo en este idioma estará disponible pronto — se muestra la versión en inglés.',
    'pt-BR': 'O vídeo neste idioma chega em breve — versão em inglês exibida.',
    ar: 'الفيديو بهذه اللغة قادم قريبًا — يتم عرض النسخة الإنجليزية.',
    'ar-SY': 'الفيديو بهاللغة رح يجي قريبًا — عم تنعرض النسخة الإنكليزية.',
    zh: '该语言的视频即将推出——当前显示英文版本。',
  },
};
